import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import type { ContractTemplate, VariablesData, TemplateListItem, CustomVariable } from './contract-template/types';
import TemplateEditor from './contract-template/TemplateEditor';

// ── Main Component (orchestrator) ────────────────────────────────────────────

export default function ContractTemplateSection() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => setIsFullscreen(f => !f), []);

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: templateData, isLoading } = useQuery<ContractTemplate>({
    queryKey: ['contract-template', selectedTemplateId],
    queryFn: () => apiGet(`/contract-template${selectedTemplateId ? `?templateId=${selectedTemplateId}` : ''}`),
  });

  const { data: variablesData } = useQuery<VariablesData>({
    queryKey: ['contract-template-variables', selectedTemplateId],
    queryFn: () => apiGet(`/contract-template/variables${selectedTemplateId ? `?templateId=${selectedTemplateId}` : ''}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const { data: templateList = [], isLoading: isLoadingTemplates } = useQuery<TemplateListItem[]>({
    queryKey: ['contract-template-list'],
    queryFn: async () => {
      const res = await apiGet<TemplateListItem[]>('/contract-template/list');
      return res || [];
    },
    staleTime: 30_000,
  });

  // Auto-select default template on load
  useEffect(() => {
    if (selectedTemplateId === null && templateList.length > 0) {
      const defaultTemplate = templateList.find(t => t.isDefault);
      setSelectedTemplateId(defaultTemplate?.id || templateList[0]?.id || null);
    }
  }, [templateList, selectedTemplateId]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const newTemplateMutation = useMutation({
    mutationFn: (name: string) => apiPost('/contract-template', { name }),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['contract-template-list'] });
      setSelectedTemplateId(data.data?.id || null);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/contract-template/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-template-list'] });
      const next = templateList.find(t => t.isDefault);
      setSelectedTemplateId(next?.id || null);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/contract-template/${id}/set-default`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-template-list'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => apiPost('/contract-template/duplicate', { templateId: selectedTemplateId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract-template-list'] }); toast.success('Template duplicato'); },
    onError: () => toast.error('Errore duplicazione'),
  });

  const seedDefaultMutation = useMutation({
    mutationFn: () => apiPost('/contract-template/seed-default', { templateId: selectedTemplateId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract-template'] }); toast.success('Template base caricato'); },
    onError: (e: Error) => toast.error(e.message || 'Errore caricamento template'),
  });

  const addMutation = useMutation({
    mutationFn: () => apiPost('/contract-template/sections', {
      templateId: selectedTemplateId,
      title: 'Nuova Sezione',
      body: '',
      order: (template?.sections?.length ?? 0),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contract-template'] }); toast.success('Sezione aggiunta'); },
    onError: () => toast.error('Errore creazione sezione'),
  });

  // ── Template data ─────────────────────────────────────────────────────────

  const template: ContractTemplate = templateData ?? { id: '', name: '', title: null, isUsed: false, customVariables: [], sections: [] };

  const [contractTitle, setContractTitle] = useState<string | null>(template.title ?? null);
  useEffect(() => { setContractTitle(template.title ?? null); }, [template.title]);

  const handleTitleChange = useCallback((value: string) => {
    const v = value || null;
    setContractTitle(v);
    const path = `/contract-template${selectedTemplateId ? `?templateId=${selectedTemplateId}` : ''}`;
    apiPatch(path, { title: v }).catch(() => toast.error('Errore salvataggio titolo'));
  }, [selectedTemplateId]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <TemplateEditor
      template={template}
      variablesData={variablesData}
      templateList={templateList}
      selectedTemplateId={selectedTemplateId}
      onSelectTemplate={setSelectedTemplateId}
      isAdmin={isAdmin}
      isLoading={isLoading}
      isLoadingTemplates={isLoadingTemplates}
      onNewTemplate={name => newTemplateMutation.mutateAsync(name)}
      onDeleteTemplate={id => deleteTemplateMutation.mutateAsync(id)}
      onSetDefault={id => setDefaultMutation.mutateAsync(id)}
      onDuplicateTemplate={() => duplicateMutation.mutate()}
      isDuplicating={duplicateMutation.isPending}
      onSeedDefault={() => seedDefaultMutation.mutate()}
      isSeeding={seedDefaultMutation.isPending}
      onAddSection={() => addMutation.mutate()}
      isAddingSection={addMutation.isPending}
      contractTitle={contractTitle}
      onTitleChange={handleTitleChange}
      isFullscreen={isFullscreen}
      toggleFullscreen={toggleFullscreen}
      onTemplateImported={(id) => setSelectedTemplateId(id)}
    />
  );
}
